// Libraries
import { Flex, Text } from '@chakra-ui/react';
// Components
import { RoundedArrowIcon, TextWithTooltip } from '@atoms/index';
// Utils
import text from '@translations/en.json';
// Types
import { EmissionsItemProps } from './EmissionsItem.types';

/**
 * A component that displays emissions information in a formatted way.
 *
 * @component
 * @param {EmissionsItemProps} props - Properties passed down to the component
 * @param {string} props.title - The title of the emission item
 * @param {string} props.tooltipText - Tooltip text displayed when hovering over the item title
 * @param {number} props.value - The numerical value of the emissions item
 * @param {string} props.formattedValue - The value of the emissions item, formatted for display
 *
 * @returns {ReactElement} A block of emissions information, including title, value, and comparison arrow.
 */
export const EmissionsItem = ({ title, tooltipText, value, formattedValue }: EmissionsItemProps) => {
  const positive = { color: 'error.base', transform: 'rotate(-90deg)' };
  const negative = { color: 'base', transform: 'rotate(90deg)' };
  const arrowStyles = value > 0 ? positive : negative;
  const adverb = value > 0 ? text.more : text.less;
  const message = `${adverb} ${text.than_forecasted}`;
  const valueMl = !!value ? 0 : 8;

  return (
    <Flex direction='column' p={{ base: 2, md: 3 }} bg='white' h={{ base: 20, md: 26 }}>
      <TextWithTooltip
        color='text.base'
        fontSize={{ base: 's', md: 'm' }}
        fontWeight='light'
        ml={{ base: 0, md: 1 }}
        tooltipText={tooltipText}
        whiteSpace='nowrap'
      >
        {title}
      </TextWithTooltip>
      <Flex align='center' gap={2}>
        {!!value && <RoundedArrowIcon boxSize='6' {...arrowStyles} />}

        <Text color='grey.800' fontSize={{ base: 'xl', md: 'hl' }} ml={valueMl}>
          {formattedValue}
        </Text>
      </Flex>
      {!!value && (
        <Text color='text.faded' fontSize='s' ml={{ base: 0, md: 9 }}>
          {message}
        </Text>
      )}
    </Flex>
  );
};
